import { useCallback, useMemo, useState } from 'react'
import BigNumber from 'bignumber.js'
import {
  getAssociatedTokenAddress,
  getMint,
  NATIVE_MINT
} from '@solana/spl-token'
import { useConnection, useWallet } from '@solana/wallet-adapter-react'
import { PublicKey } from '@solana/web3.js'

const useBalance = (mint?: PublicKey | string) => {
  const { publicKey } = useWallet()
  const { connection } = useConnection()

  const [balance, setBalance] = useState<number>()
  const [decimals, setDecimals] = useState<number>()

  const memoMint = useMemo(() => {
    if (!mint) mint = NATIVE_MINT
    else if (!(mint instanceof PublicKey)) mint = new PublicKey(mint)

    return mint
  }, [])

  const uiBalance = useMemo(
    () =>
      balance && decimals
        ? new BigNumber(balance).dividedBy(Math.pow(10, decimals)).toString()
        : '0.00',
    [balance, decimals]
  )

  const getDecimals = useCallback(async () => {
    const mintInfo = await getMint(connection, memoMint)
    setDecimals(mintInfo.decimals)
  }, [connection, memoMint])

  const getBalance = useCallback(async () => {
    if (!publicKey) {
      setBalance(undefined)
      return
    }
    getDecimals()

    let balance: number
    if (memoMint.toBase58() === NATIVE_MINT.toBase58()) {
      balance = await connection.getBalance(publicKey)
    } else {
      const ata = await getAssociatedTokenAddress(memoMint, publicKey)
      balance = await connection
        .getTokenAccountBalance(ata)
        .then((resp) => Number(resp.value.amount))
    }
    setBalance(balance)
  }, [connection, getDecimals, memoMint, publicKey])

  return {
    balance,
    uiBalance,
    decimals,
    getBalance
  }
}

export default useBalance
